import type { InflationDataItem, InflationDataMap } from '@wix/thunderbolt-becky-root'
import type {
	ResolvedPresetsRefArray,
	ResolvedRefArray,
	ResolvedVariantRelation,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
	VariantDataItem,
	VariantRelationOnlyRefArray,
} from '@wix/thunderbolt-becky-types'
import type { BaseDataItem, Component } from '@wix/thunderbolt-becky-types/src/structureTypes'
import { Unref, removeHash } from '@wix/thunderbolt-catharsis'
import { getMultipleRefCompsIdFromInflatedId, getUniqueId, REF_DELIMITER } from '@wix/thunderbolt-commons'
import _ from 'lodash'
import { createRefInResolvedRefsNamespaces, isInflatedId } from '../byRefUtils'
import { RESOLVED_REF_COMP_PRESETS_ASPECT, VARIANTS_TYPES_TO_INFLATE } from '../constants'
import type { DataRefsForPresetResolving, GetOverrideDataRefs } from './types'
import { getDataItemRefsWithOverrides } from './dataConsumption'
import { ScopedCreateRef } from '../compNodeUtils'

export const getRefCompResolvedPresetsRef = (refCompInflatedId: string) =>
	createRefInResolvedRefsNamespaces<Array<ResolvedPresetsRefArray['values']>>(
		RESOLVED_REF_COMP_PRESETS_ASPECT,
		refCompInflatedId,
		[]
	)

export const getPresetsRefsWithAncestors = (
	templateRefComp: Component,
	refCompContext: string,
	createRefInRefCompNamespace: ScopedCreateRef,
	getAncestorsOverrideDataRefs: GetOverrideDataRefs
) => {
	return {
		ownPresets: getDataItemRefsWithOverrides({
			templateComponent: templateRefComp,
			queryName: 'presetsQuery',
			createRefInTemplateNamespace: createRefInRefCompNamespace,
			getOverrideDataRefs: getAncestorsOverrideDataRefs,
		}),
		parentRefCompResolvedPresets: refCompContext
			? createRefInResolvedRefsNamespaces(RESOLVED_REF_COMP_PRESETS_ASPECT, refCompContext)
			: [],
	}
}

export const getInflatedDataItemsMap = (
	{
		refArraysWithOverrides,
		leafsOverridesMaps,
		parentRefCompResolvedPresets,
		experiments,
	}: Unref<DataRefsForPresetResolving>,
	inflatedDataQuery: string,
	pageVariants: Record<string, any>,
	dataItemType: string
) => {
	const dataItemsWithOverridesMap = Object.assign(
		_.keyBy(refArraysWithOverrides.filter(Boolean), 'id'),
		...leafsOverridesMaps
	)

	const resolvedTemplateRefArray = _.last(refArraysWithOverrides)! as ResolvedRefArray<InflationDataItem>

	const consumerRefArray = (refArraysWithOverrides[0] || {}) as ResolvedRefArray<InflationDataItem>
	// remove compact once this is fixed: https://jira.wixpress.com/browse/DM-7278 (there should not be undefined values)
	const consumerRefArrayValuesIds = consumerRefArray.values
		? _(consumerRefArray.values)
				.compact()
				.map(({ id }) => id)
				.value()
		: []

	return inflateRefArrayWithPresets(
		resolvedTemplateRefArray,
		inflatedDataQuery,
		parentRefCompResolvedPresets,
		dataItemsWithOverridesMap,
		dataItemType,
		pageVariants,
		consumerRefArrayValuesIds,
		experiments
	)
}

/**
 * In cases where items are inflated by preset-override, they don't have the full inflated id.
 * E.g., the inflated component's Id is `pageRefCompId_r_widgetRefCompId_r_compId`,
 * but the style\layout item's id is `widgetRefCompId_r_compId-styleId-presetData123-style` (missing the page refComp id prefix)
 * The viewer doesn't mind this (and the id is still unique in the page), but this causes issues in DM,
 * because DM thinks those items are orphans, and it deletes them during save (aka styles GC).
 * So we need to make sure that the ids have the full compId prefix.
 * @param item
 * @param inflatedDataQuery
 * @returns
 */
const ensureFullInflatedOverrideDataQuery = <
	Item extends InflationDataItem | ResolvedVariantRelation<InflationDataItem>
>(
	refCompHasPresets: boolean,
	consumerRefArrayValuesIds: Array<string>,
	item: Item,
	inflatedDataQuery: string,
	dataItemType: string,
	hasCompPreset: boolean
): Item => {
	const { id, type } = item
	if (
		(hasCompPreset && !isInflatedId(id)) || // is not sharedPart and override saved as new dataItem
		id.startsWith(inflatedDataQuery) || // already inflated
		consumerRefArrayValuesIds.includes(id) // is an override in the consumer refArray
	) {
		return item
	}
	const styleIdOrLayoutQuery = _.last(inflatedDataQuery.split('-')) as 'styleId' | 'layoutQuery'
	const itemSuffix = id.split(styleIdOrLayoutQuery)[1] || `-${refCompHasPresets ? dataItemType : id}` // e.g. -presetData123-style

	return {
		...item,
		id: `${inflatedDataQuery}${itemSuffix}`,
		...(type === 'VariantRelation' && {
			to: ensureFullInflatedOverrideDataQuery(
				refCompHasPresets,
				consumerRefArrayValuesIds,
				item.to,
				inflatedDataQuery,
				dataItemType,
				hasCompPreset
			),
		}),
	}
}

const filterUnresolvedValues = (
	refArrayValues: ResolvedRefArray<InflationDataItem>['values'],
	pageVariants: Record<string, true>,
	refCompHasPresets: boolean,
	consumerRefArrayValuesIds: Array<string>,
	inflatedDataQuery: string,
	dataItemType: string,
	hasCompPreset: boolean
) => {
	// Filter variantRelations from other presets
	const resolvedVariantRelations = refArrayValues.filter(
		(value) =>
			value.type === 'VariantRelation' &&
			value.variants.every((variant) => {
				const variantId = ((variant as unknown) as VariantDataItem).id
				return pageVariants[variantId] || isInflatedId(variantId)
			})
	)

	// Filter variant relations with the same variants array (taking the first, which must be the result of preset override because of the order of the preset resolving which is inside out)
	const uniqueVariantRelations = _.uniqWith(
		resolvedVariantRelations,
		(x, y) =>
			x.type === 'VariantRelation' &&
			y.type === 'VariantRelation' &&
			_.isEqual(
				x.variants
					.map((variant) => ((variant as unknown) as VariantDataItem).id)
					.concat(x.remoteVariants?.map((variantId) => removeHash(variantId)) || [])
					.sort(),
				y.variants
					.map((variant) => ((variant as unknown) as VariantDataItem).id)
					.concat(y.remoteVariants?.map((variantId) => removeHash(variantId)) || [])
					.sort()
			)
	)

	// At this point we have 1+ default items in refArrayValues
	// The first one is the default item in the original style\layout refArray, we should filter it out.
	// If we have preset override that was made in desktop, we'll have a default style\layout items somewhere in the middle.
	// We can have 1 default style\layout item that generated from presetOverride for each level of widget.
	// If we are in the consumer (EditorX) then the last default item is the one that comes from the preset selected in desktop.
	// The order of priority is: inner presetOverride > outer presetOverride > desktop
	const numberOfDefaultValues = refArrayValues.filter((val) => val.type !== 'VariantRelation').length
	if (numberOfDefaultValues > 1) {
		const firstDefaultItemIndex = refArrayValues.findIndex((val) => val.type !== 'VariantRelation')
		refArrayValues.splice(firstDefaultItemIndex, 1)
	}
	const lastDefaultItem = _.find(refArrayValues, (val) => val.type !== 'VariantRelation') as InflationDataItem

	const refArray = lastDefaultItem ? [lastDefaultItem, ...uniqueVariantRelations] : uniqueVariantRelations

	return refArray.map((item) =>
		ensureFullInflatedOverrideDataQuery(
			refCompHasPresets,
			consumerRefArrayValuesIds,
			item,
			inflatedDataQuery,
			dataItemType,
			hasCompPreset
		)
	)
}

// "ref1_r_ref2_r_compId-query => ["ref2_r_compId-query", "ref1_r_ref2_r_compId-query"]
const getQueries = (dataQuery: string): Array<string> => {
	const res: Array<string> = []
	_(dataQuery)
		.split(REF_DELIMITER)
		.reverse()
		.forEach((refId, index) => {
			if (index > 0) {
				res.push(`${refId}${REF_DELIMITER}${res[index - 1]}`)
			} else {
				res.push(refId)
			}
		})
	res.shift()
	return res
}

// Returns the item under the preset condition.
// If there is no item under the preset condition, returns the first item which isn't not under any condition.
const getItemsUnderPresetCondition = (
	presetCondition: string,
	refArrayValues: ResolvedRefArray<InflationDataItem>['values'],
	enableGlobalVariant: boolean
): ResolvedRefArray<InflationDataItem>['values'] => {
	const getDefaultItem = () =>
		// there's no default value for reaction data items
		(_.findLast(refArrayValues, (value) => value.type !== 'VariantRelation') as InflationDataItem) || []

	let hasDefaultPresetValue = false
	const variantRelationWithPresetCondition = _(refArrayValues)
		.map((value) => {
			const containPreset =
				value.type === 'VariantRelation' &&
				value.variants.some((variant) => ((variant as unknown) as VariantDataItem).id === presetCondition)

			if (containPreset) {
				// default value of preset
				if (value.variants.length === 1) {
					hasDefaultPresetValue = true
					return value.to
				}

				return {
					...value,
					variants: value.variants.filter(
						(variant) => ((variant as unknown) as VariantDataItem).type !== 'Preset'
					),
				}
			}
			// variant which are not connected to preset

			const globalVariantRelation =
				value.type === 'VariantRelation' &&
				value.variants.every((variant) => ((variant as unknown) as VariantDataItem).type !== 'Preset')
			if (enableGlobalVariant && globalVariantRelation) {
				return value
			}
			return undefined
		})
		.compact()
		.value()

	return hasDefaultPresetValue
		? variantRelationWithPresetCondition
		: variantRelationWithPresetCondition.concat(getDefaultItem())
}

const inflateVariantIds = (variants: Array<VariantDataItem>, ancestorsChain: string) => {
	return variants.map((variant) => {
		return VARIANTS_TYPES_TO_INFLATE.includes(variant.type) && !variant.id.startsWith(ancestorsChain)
			? {
					...variant,
					id: getUniqueId(ancestorsChain, variant.id),
			  }
			: variant
	})
}

const inflateVariantRelation = (
	id: string,
	value: ResolvedVariantRelation<InflationDataItem>,
	ancestorsChain: string,
	dataQuery: string,
	toOverride: boolean = false
) => {
	// inflate variant relations since variant data item point to specific component
	const variants = inflateVariantIds((value.variants as unknown) as Array<VariantDataItem>, ancestorsChain)
	const toId = `${dataQuery}-${removeHash(value.to.id)}`
	return ({
		...value,
		to: { ...value.to, id: toOverride ? value.to.id : toId },
		id,
		variants,
		from: `#${getUniqueId(ancestorsChain, removeHash(value.from))}`,
	} as unknown) as ResolvedVariantRelation<InflationDataItem>
}

const resolveItemsUnderPreset = (
	presetDataItem: ResolvedPresetsRefArray['values'][number],
	dataItemType: string,
	resolvedRefArrayValues: ResolvedRefArray<InflationDataItem>['values'],
	queries: Array<string>,
	dataQuery: string,
	mergedDataMap: InflationDataMap,
	ancestorsChain: string,
	enableGlobalVariant: boolean
): ResolvedRefArray<InflationDataItem>['values'] => {
	const presetItemKey = dataItemType === 'style' ? 'style' : 'layout'
	if (presetDataItem.type === 'PresetData') {
		const resolvedItemsUnderPreset = getItemsUnderPresetCondition(
			presetDataItem[presetItemKey],
			resolvedRefArrayValues,
			enableGlobalVariant
		)

		return resolvedItemsUnderPreset.map((value) => {
			if (value.type === 'VariantRelation') {
				const variantIds = ((value.variants as unknown) as Array<VariantDataItem>)
					.map((variant: BaseDataItem) => variant.id)
					.join('-')
				const id = `${presetDataItem.id}-${variantIds}-${dataItemType}`

				// find item override in all ancestors and take the last one (the most outer override)
				const itemOverride = _.map(queries, (query) => mergedDataMap[`${query}-${id}`])
					.filter((item) => item)
					.pop() as InflationDataItem
				const toOverride = _.map(queries, (query) => mergedDataMap[`${query}-${value.to.id}`])
					.filter((item) => item)
					.pop() as InflationDataItem

				return itemOverride
					? itemOverride
					: inflateVariantRelation(
							`${dataQuery}-${id}`,
							{ ...value, to: toOverride ? toOverride : value.to },
							ancestorsChain,
							dataQuery,
							toOverride ? true : false
					  )
			} else {
				const id = `${presetDataItem.id}-${dataItemType}`
				// find item override in all ancestors and take the last one (the most outer override)
				const itemOverride = _.map(queries, (query) => mergedDataMap[`${query}-${id}`])
					.filter((item) => item)
					.pop() as InflationDataItem

				return itemOverride ? itemOverride : { ...value, id: `${dataQuery}-${id}` }
			}
		}) as ResolvedRefArray<InflationDataItem>['values']
	}
	// not default
	const presetItem = presetDataItem.to
	const inflatedVariantRelationId = `${dataQuery}-${presetDataItem.id}`

	const resolvedItemsUnderPreset = getItemsUnderPresetCondition(
		presetItem[presetItemKey],
		resolvedRefArrayValues,
		enableGlobalVariant
	)

	return resolvedItemsUnderPreset.map((item) => {
		const variants = ((item as ResolvedVariantRelation<any>)?.variants as unknown) as Array<VariantDataItem>
		const variantIds = variants?.map((variant) => variant.id).join('-')
		const id = variants?.length >= 1 ? `${presetDataItem.id}-${variantIds}` : presetDataItem.id

		const variantRelationOverride = _.map(queries, (query) => mergedDataMap[`${query}-${id}`])
			.filter((override) => override)
			.pop() as UnresolvedVariantRelation

		if (variantRelationOverride) {
			// If we have override for the variant relation we should use it and just inflate the IDs in order
			// to be able to use the same widget more than one time in the same page
			const variantRelationToOverride = _.map(queries, (query) => mergedDataMap[`${query}-${id}-${dataItemType}`])
				.filter((override) => override)
				.pop() as InflationDataItem
			// We resolve the "to" in order to be consistent with the flow when there is no override
			return {
				...variantRelationOverride,
				to: variantRelationToOverride,
			}
		}

		const inflatedItemId = `${id}-${dataItemType}`
		const itemOverride = _.map(queries, (query) => mergedDataMap[`${query}-${id}-${dataItemType}`])
			.filter((override) => override)
			.pop() as InflationDataItem

		if (item.type === 'VariantRelation') {
			const inflated = inflateVariantRelation(inflatedItemId, item, ancestorsChain, dataQuery)

			return {
				...presetDataItem,
				from: `#${getUniqueId(dataQuery, removeHash(presetDataItem.from))}`,
				variants: [...presetDataItem.variants, ...inflated.variants],
				id: `${dataQuery}-${id}`,
				to: itemOverride
					? itemOverride
					: {
							...inflated.to,
							id: `${dataQuery}-${inflatedItemId}`,
					  },
			}
		} else {
			return {
				...presetDataItem,
				id: inflatedVariantRelationId,
				to: itemOverride
					? itemOverride
					: {
							...item,
							id: `${dataQuery}-${inflatedItemId}`,
					  },
			}
		}
	}) as ResolvedRefArray<InflationDataItem>['values']
}

const compareArrays = (arr1: Array<string> = [], arr2: Array<string> = []) =>
	arr1.length === arr2.length && arr1.every((value) => arr2.includes(value))

// When merging overrides ref array into ref array we need to filter out duplicate variant relations.
// Duplicate variant relation is 2 variant relation with the same variants array.
const mergeRefArrayOverrides = (
	values: ResolvedRefArray<InflationDataItem>['values'],
	overrideValues: VariantRelationOnlyRefArray<InflationDataItem>['values']
) => {
	const filteredValues = values.filter((value) => {
		if (value.type !== 'VariantRelation') {
			return true
		}

		const variantsIds = value.variants.map((variant) => ((variant as unknown) as VariantDataItem).id)
		return (
			value.type !== 'VariantRelation' ||
			!overrideValues.find((overrideValue) =>
				compareArrays(
					overrideValue.variants
						.map((variant) => ((variant as unknown) as VariantDataItem).id)
						.concat(overrideValue.remoteVariants?.map((variantId) => removeHash(variantId)) || []),
					variantsIds
				)
			)
		)
	})
	return [...filteredValues, ...overrideValues]
}

const inflateRefArrayWithPresets = (
	resolvedTemplateRefArray: ResolvedRefArray<InflationDataItem>,
	inflatedDataQuery: string,
	refCompPresets: Readonly<Array<ResolvedPresetsRefArray['values']>>,
	resolvedMergedDataMap: InflationDataMap,
	dataItemType: string,
	pageVariants: Record<string, true>,
	consumerRefArrayValuesIds: Array<string>,
	experiments: Record<string, boolean>
) => {
	const queries = getQueries(inflatedDataQuery)
	const hasCompPreset = refCompPresets.length > 0
	const ancestorsChain = getMultipleRefCompsIdFromInflatedId(inflatedDataQuery)
	const originalValues = resolvedTemplateRefArray.values
	const originalWithoutVariantRelations = resolvedTemplateRefArray.values.filter(
		(item) => item.type !== 'VariantRelation'
	)
	const enableGlobalVariant = experiments.layouterNavigationConsumption
	const resolvedRefArrayValuesWithPresetsApplied = hasCompPreset
		? refCompPresets.reduce((previousResolvedRefArrayValues, presetsArray, index) => {
				const dataQuery = queries[index]
				// Can be smarter than just concat. Currently, we filter unneeded values using filterUnresolvedValues
				const resolveRefArrayValuesWithPresetsApplied = previousResolvedRefArrayValues.concat(
					presetsArray
						.filter((presetDataItem) => presetDataItem)
						.flatMap((presetDataItem) =>
							resolveItemsUnderPreset(
								presetDataItem,
								dataItemType,
								originalValues.concat(previousResolvedRefArrayValues),
								queries.slice(index),
								dataQuery,
								resolvedMergedDataMap,
								ancestorsChain,
								enableGlobalVariant
							)
						)
				)

				const inflatedAndResolvedOverridesRefArray = resolvedMergedDataMap[
					dataQuery
				] as ResolvedRefArray<InflationDataItem>
				if (!inflatedAndResolvedOverridesRefArray || inflatedAndResolvedOverridesRefArray.type !== 'RefArray') {
					return resolveRefArrayValuesWithPresetsApplied
				}

				const resolvedValuesWithPresetsAndOverridesApplied = mergeRefArrayOverrides(
					resolveRefArrayValuesWithPresetsApplied,
					inflatedAndResolvedOverridesRefArray.values.filter(
						// remove optional chaining operation (?.) once this is fixed: https://jira.wixpress.com/browse/DM-7278
						(item) => item?.type === 'VariantRelation'
					) as VariantRelationOnlyRefArray<InflationDataItem>['values']
				)

				return resolvedValuesWithPresetsAndOverridesApplied
		  }, originalWithoutVariantRelations)
		: // inflate variant relation for internal refs
		  resolvedTemplateRefArray.values.map((item) =>
				item.type === 'VariantRelation'
					? inflateVariantRelation(item.id, item, ancestorsChain, inflatedDataQuery)
					: item
		  )

	let refArrayValues = resolvedRefArrayValuesWithPresetsApplied

	/*
	support new override mechanism:
	new dataItem is created for each override with a new id (not inflated)
	an override of the refArray (with the inflated id) is created with the new dataItem
	*/
	const refArrayOverride = resolvedMergedDataMap[inflatedDataQuery] as ResolvedRefArray<InflationDataItem>
	const defaultOverrideValue = _.findLast(refArrayOverride?.values, (item) => item?.type !== 'VariantRelation')

	// remove all other default values and leave only the override one
	if (defaultOverrideValue) {
		refArrayValues = _(resolvedRefArrayValuesWithPresetsApplied)
			.filter((item) => item?.type === 'VariantRelation')
			.push(defaultOverrideValue)
			.value()
	}

	const enableOverrides = experiments.interactionsOverrides
	return getDataOverrides(
		{
			...resolvedTemplateRefArray,
			values: filterUnresolvedValues(
				refArrayValues,
				pageVariants,
				refCompPresets.length > 0,
				consumerRefArrayValuesIds,
				inflatedDataQuery,
				dataItemType,
				hasCompPreset
			),
		},
		inflatedDataQuery,
		enableOverrides
	)
}

const getDataOverrides = (
	resolvedRefArray: ResolvedRefArray<InflationDataItem>,
	dataQuery: string,
	enableOverrides: boolean
) => {
	const dataOverrides: Record<string, UnresolvedRefArray | UnresolvedVariantRelation | InflationDataItem> = {}
	dataOverrides[dataQuery] = {
		...resolvedRefArray,
		id: dataQuery,
		values: resolvedRefArray.values.map((value) => `#${value.id}`),
	}

	resolvedRefArray.values.forEach((value) => {
		if (value.type === 'VariantRelation') {
			const remoteVariants =
				enableOverrides && value.remoteVariants ? value.remoteVariants.map((id) => removeHash(id)) : []
			dataOverrides[value.id] = {
				...value,
				to: `#${value.to.id}`,
				variants: value.variants
					.map((variant) => ((variant as unknown) as VariantDataItem).id)
					.concat(remoteVariants),
			}
			dataOverrides[value.to.id] = value.to
		} else {
			dataOverrides[value.id] = value
		}
	})
	return dataOverrides
}
